import { isDefined, isNullOrUndefined } from "@clipboard-health/util-ts";
import { useEffect, useRef } from "react";

import { showErrorToast } from "../lib/Notifications";
import {
  type DocumentDownloadStatusResponse,
  useGetDocumentDownloadStatus,
} from "./api/useGetDocumentDownloadStatus";
import { DownloadRequestStatus } from "./types/common";

interface StartPollingProps {
  requestedByUserId: string;
  documentDownloadId: string;
  onPollingSuccess?: (data: DocumentDownloadStatusResponse) => Promise<void>;
  onPollingFailure?: (data?: DocumentDownloadStatusResponse) => Promise<void>;
  onStatusChange?: (status: DownloadRequestStatus) => void;
}

interface PollingState {
  documentId: string;
  startTime: number;
}

const POLLING_INTERVAL_IN_SECONDS = 3;
const POLLING_EXPIRY_TIME_IN_SECONDS = 60 * 5;
const LOCAL_STORAGE_KEY = "workerDocumentDownloadPollingRequest";

function getLocalStorageKey(requestedByUserId: string): string {
  return `${LOCAL_STORAGE_KEY}-${requestedByUserId}`;
}

function getPollingStateFromLocalStorage(requestedByUserId: string): PollingState | undefined {
  const pollingState = localStorage.getItem(getLocalStorageKey(requestedByUserId));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return isDefined(pollingState) ? JSON.parse(pollingState) : undefined;
}

function setPollingStateInLocalStorage(
  documentDownloadId: string,
  requestedByUserId: string
): void {
  const pollingState: PollingState = {
    documentId: documentDownloadId,
    startTime: Date.now(),
  };
  localStorage.setItem(getLocalStorageKey(requestedByUserId), JSON.stringify(pollingState));
}

export function usePollingDocumentDownloadRequest() {
  const pollingTimeoutId = useRef<NodeJS.Timeout | undefined>(undefined);
  const { mutateAsync: getDocumentDownloadRequest } = useGetDocumentDownloadStatus();

  useEffect(
    () => () => {
      if (isDefined(pollingTimeoutId.current)) {
        clearInterval(pollingTimeoutId.current);
        pollingTimeoutId.current = undefined;
      }
    },
    []
  );

  function clearPolling(requestedByUserId: string) {
    if (isDefined(pollingTimeoutId.current)) {
      clearInterval(pollingTimeoutId.current);
      pollingTimeoutId.current = undefined;
    }

    localStorage.removeItem(getLocalStorageKey(requestedByUserId));
  }

  function hasActiveDownloadRequest(requestedByUserId: string) {
    const pollingState = getPollingStateFromLocalStorage(requestedByUserId);
    if (isDefined(pollingState)) {
      const timeElapsed = (Date.now() - pollingState.startTime) / 1000;
      if (timeElapsed > POLLING_EXPIRY_TIME_IN_SECONDS) {
        clearPolling(getLocalStorageKey(requestedByUserId));
        return false;
      }

      return true;
    }

    return false;
  }

  async function pollDocumentStatus(props: StartPollingProps) {
    const {
      documentDownloadId,
      requestedByUserId,
      onPollingSuccess,
      onPollingFailure,
      onStatusChange,
    } = props;
    try {
      const response = await getDocumentDownloadRequest({ documentDownloadId });

      const { status } = response.data.attributes;

      if (isDefined(status)) {
        onStatusChange?.(status);

        if (status === DownloadRequestStatus.COMPLETED) {
          clearPolling(requestedByUserId);
          if (isDefined(onPollingSuccess)) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            onPollingSuccess(response);
          }
        } else if (status === DownloadRequestStatus.FAILED) {
          clearPolling(requestedByUserId);
          if (isDefined(onPollingFailure)) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            onPollingFailure(response);
          }
        } else if (isNullOrUndefined(pollingTimeoutId.current)) {
          const timeoutId = setInterval(async () => {
            await pollDocumentStatus(props);
          }, POLLING_INTERVAL_IN_SECONDS * 1000);
          pollingTimeoutId.current = timeoutId;
        }
      }
    } catch {
      clearPolling(requestedByUserId);
      if (isDefined(onPollingFailure)) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        onPollingFailure();
      }
    }
  }

  async function startPolling(props: StartPollingProps) {
    const { documentDownloadId, requestedByUserId } = props;

    const pollingState = getPollingStateFromLocalStorage(requestedByUserId);

    if (isDefined(pollingState)) {
      const { startTime } = pollingState;
      const timeElapsed = (Date.now() - startTime) / 1000;

      if (timeElapsed < POLLING_EXPIRY_TIME_IN_SECONDS) {
        showErrorToast(
          "There is already a download in progress, please wait for it to complete before downloading again"
        );
        return;
      }
    }

    clearPolling(requestedByUserId);
    setPollingStateInLocalStorage(documentDownloadId, requestedByUserId);
    await pollDocumentStatus(props);
  }

  return {
    startPolling,
    hasActiveDownloadRequest,
    cancelPollingRequest: clearPolling,
  };
}
